import React, { PropsWithChildren } from 'react';

import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import { NavigationContext, NavigationContextType } from 'src/app/navigation';
import { NavigationWrapper } from 'src/app/navigation-wrapper';
import { DEFAULT_TITLE, TITLE_TEMPLATE } from 'src/constants/helmet';
import { loadProjectPage, UNLOAD_PROJECT } from 'src/redux/project';
import { Dispatch } from 'src/types/state';

import { ProjectNavigation } from './containers/navigation';
import { RouteProps } from './types';

type Props = PropsWithChildren<
  RouteProps & {
    dispatch: Dispatch;
    isNavigationRestructureEnabled: boolean;
  }
>;

let navContextValue: NavigationContextType = {
  navigationType: 'container',
  mobileHeading: '',
};

class ProjectRoute extends React.Component<Props> {
  componentDidMount() {
    this.loadPage();
  }

  componentDidUpdate(prevProps: Props) {
    const projectName = this.getFullProjectSlug();
    const prevProjectName = this.getFullProjectSlug(prevProps.match.params);

    if (projectName !== prevProjectName) {
      // Child routes might try to load data based on the project in redux. To
      // short circuit any race conditions, we need to remove the old project
      // from redux before we try to load the new one.
      this.unloadPage();
      this.loadPage();
    }
  }

  componentWillUnmount() {
    this.unloadPage();
  }

  loadPage() {
    const workspaceSlug = this.getWorkspaceSlug();
    const projectKey = this.getProjectKey();
    this.props.dispatch(loadProjectPage(workspaceSlug, projectKey));
  }

  unloadPage() {
    this.props.dispatch({ type: UNLOAD_PROJECT });
  }

  getWorkspaceSlug(params = this.props.match.params) {
    const { workspaceSlug } = params;
    if (typeof workspaceSlug === 'string') {
      return workspaceSlug;
    } else {
      return 'Workspace';
    }
  }

  getProjectKey(params = this.props.match.params) {
    const { projectKey } = params;
    if (typeof projectKey === 'string') {
      return projectKey;
    } else {
      return 'Project';
    }
  }

  getFullProjectSlug(params = this.props.match.params) {
    const workspaceSlug = this.getWorkspaceSlug(params);
    const projectKey = this.getProjectKey(params);
    return `${workspaceSlug} / ${projectKey}`;
  }

  renderTitle() {
    const fullProjectSlug = this.getFullProjectSlug();
    const defaultTitle = `${fullProjectSlug} - ${DEFAULT_TITLE}`;
    const titleTemplate = `${fullProjectSlug} ${TITLE_TEMPLATE}`;

    return (
      <Helmet
        defer={false}
        defaultTitle={defaultTitle}
        titleTemplate={titleTemplate}
      />
    );
  }

  render() {
    const fullProjectSlug = this.getFullProjectSlug();

    if (this.props.isNavigationRestructureEnabled) {
      if (navContextValue.mobileHeading !== fullProjectSlug) {
        // Update the object reference
        navContextValue = {
          navigationType: 'container',
          mobileHeading: fullProjectSlug,
        };
      }

      return (
        <NavigationContext.Provider value={navContextValue}>
          {this.renderTitle()}
          {this.props.children}
        </NavigationContext.Provider>
      );
    }

    return (
      <NavigationWrapper
        Component={ProjectNavigation}
        mobileHeading={fullProjectSlug}
      >
        {this.renderTitle()}
        {this.props.children}
      </NavigationWrapper>
    );
  }
}

export default connect()(ProjectRoute);
